import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import type {
  CreateWorkTypePayload,
  DeleteWorkTypeArgs,
  RearrangeWorkTypeArgs,
  UpdateWorkTypePayload,
  WorkType,
  WorkTypeDependentCount,
} from './workTypes.interface';
import { FILTER_OPTIONS_STALE_MS } from '@/constants/app-constants';

const BASE = '/ats_work_types';

/** Mutation responses carry either the work type (`data`) or validation `errors`. */
type WorkTypeMutationResponse = { data?: WorkType; errors?: string[] };

// Note: the ats-https interceptor already unwraps an axios response to its
// body, so `http.get(...)` resolves to `{ data: ... }` and we read `.data`.
export const workTypesApi = {
  list: async (): Promise<WorkType[]> => {
    const res = await http.get(BASE);
    return ((res as { data?: WorkType[] })?.data ?? []) as WorkType[];
  },

  create: async (payload: CreateWorkTypePayload): Promise<WorkType> => {
    const res = (await http.post(BASE, payload)) as WorkTypeMutationResponse;
    // Validation errors (e.g. duplicate name) come back in the body, not only
    // as an HTTP error — surface them so callers can toast the message.
    if (res?.errors?.length) throw { errors: res.errors };
    return res?.data as WorkType;
  },

  update: async ({
    id,
    payload,
  }: {
    id: number;
    payload: UpdateWorkTypePayload;
  }): Promise<WorkType> => {
    const res = (await http.put(
      `${BASE}/${id}`,
      payload
    )) as WorkTypeMutationResponse;
    if (res?.errors?.length) throw { errors: res.errors };
    return res?.data as WorkType;
  },

  rearrange: async ({
    id,
    new_position,
  }: Pick<RearrangeWorkTypeArgs, 'id' | 'new_position'>): Promise<
    WorkType[]
  > => {
    const res = await http.put(`${BASE}/${id}/rearrange`, { new_position });
    return ((res as { data?: WorkType[] })?.data ?? []) as WorkType[];
  },

  getDependentCount: async (id: number): Promise<WorkTypeDependentCount> => {
    const res = await http.get(`${BASE}/${id}/dependent_count`);
    return (
      (res as { data?: { count?: WorkTypeDependentCount } })?.data?.count ?? {
        jobs: 0,
        entities_count: 0,
      }
    );
  },

  remove: async ({
    id,
    new_work_type_id,
  }: DeleteWorkTypeArgs): Promise<void> => {
    await http.delete(
      `${BASE}/${id}`,
      new_work_type_id !== undefined
        ? { data: { new_work_type_id } }
        : undefined
    );
  },
};

export const workTypeKeys = {
  all: ['ats-work-types'] as const,
};

const reorder = <T>(list: T[], from: number, to: number): T[] => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

export const useWorkTypes = (
  options?: Omit<
    UseQueryOptions<WorkType[], Error, WorkType[]>,
    'queryKey' | 'queryFn'
  >
) =>
  useQuery({
    queryKey: workTypeKeys.all,
    queryFn: workTypesApi.list,
    // Admin-managed config read by filters, forms and table cells across the
    // app; every write below invalidates or rewrites this key, which ignores
    // staleTime, so the window only skips refetching an unchanged list.
    staleTime: FILTER_OPTIONS_STALE_MS,
    ...options,
  });

export const useCreateWorkType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: workTypesApi.create,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: workTypeKeys.all }),
  });
};

/**
 * Optimistic like reorder, and for a sharper reason than snappiness: the
 * "at least one work type must stay enabled" rule is enforced by disabling the
 * last toggle, and that check counts `enabled` rows in this cache.
 *
 * While a toggle was only invalidated on success, the cache still showed the
 * old value for the whole round trip, so toggling several off quickly measured
 * a stale count and every switch passed the check — all of them ended up off
 * (CORE-4997). Going one at a time worked because the refetch landed in
 * between. Writing the new value straight into the cache makes the count
 * reflect in-flight toggles, so the guard closes on the first click.
 */
export const useUpdateWorkType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: workTypesApi.update,
    onMutate: async ({ id, payload }) => {
      await queryClient.cancelQueries({ queryKey: workTypeKeys.all });
      const previous = queryClient.getQueryData<WorkType[]>(workTypeKeys.all);
      if (previous) {
        queryClient.setQueryData(
          workTypeKeys.all,
          previous.map((workType) =>
            workType.id === id ? { ...workType, ...payload } : workType
          )
        );
      }
      return { previous };
    },
    onError: (_error, _args, context) => {
      if (context?.previous) {
        queryClient.setQueryData(workTypeKeys.all, context.previous);
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: workTypeKeys.all }),
  });
};

export const useDeleteWorkType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: workTypesApi.remove,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: workTypeKeys.all }),
  });
};

/**
 * Reorder is one request per drop; the row order is optimistically updated in
 * the cache, reverted on error, and reconciled with the server on settle.
 */
export const useRearrangeWorkTypes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, new_position }: RearrangeWorkTypeArgs) =>
      workTypesApi.rearrange({ id, new_position }),
    onMutate: async ({ from, to }: RearrangeWorkTypeArgs) => {
      await queryClient.cancelQueries({ queryKey: workTypeKeys.all });
      const previous = queryClient.getQueryData<WorkType[]>(workTypeKeys.all);
      if (previous && from !== undefined && to !== undefined) {
        const next = reorder(previous, from, to).map((workType, index) => ({
          ...workType,
          position: index,
        }));
        queryClient.setQueryData(workTypeKeys.all, next);
      }
      return { previous };
    },
    onError: (_error, _args, context) => {
      if (context?.previous) {
        queryClient.setQueryData(workTypeKeys.all, context.previous);
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: workTypeKeys.all }),
  });
};
